import React, { useState, useEffect, useCallback } from 'react';
import {
  FaShieldAlt,
  FaBan,
  FaExclamationTriangle,
  FaSkull,
  FaUnlock,
  FaTrash,
  FaSync,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaEye,
} from 'react-icons/fa';
import { authFetch } from './authHelper';

const TAB_LABELS = {
  overview: 'Vue d\'ensemble',
  logs: 'Journal',
  bans: 'IPs bannies',
  suspicion: 'IPs suspectes',
};

const AdminSecurity = () => {
  const [tab, setTab] = useState('overview');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    const endpoint = tab === 'overview'
      ? '/api/admin/security-logs.php'
      : `/api/admin/security-logs.php?type=${tab}`;

    authFetch(endpoint)
      .then((res) => res.json())
      .then((d) => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [tab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUnban = async (ip) => {
    if (!window.confirm(`Débannir l'IP ${ip} ?`)) return;
    const res = await authFetch(`/api/admin/security-logs.php?action=unban&ip=${encodeURIComponent(ip)}`, { method: 'DELETE' });
    const d = await res.json();
    setActionMsg(d.message || d.error);
    setTimeout(() => setActionMsg(null), 3000);
    fetchData();
  };

  const handleClearSuspicion = async (ip) => {
    if (!window.confirm(`Effacer la suspicion pour ${ip} ?`)) return;
    const res = await authFetch(`/api/admin/security-logs.php?action=clear-suspicion&ip=${encodeURIComponent(ip)}`, { method: 'DELETE' });
    const d = await res.json();
    setActionMsg(d.message || d.error);
    setTimeout(() => setActionMsg(null), 3000);
    fetchData();
  };

  const formatDuration = (seconds) => {
    if (seconds <= 0) return 'Expiré';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}min`;
    return `${m}min`;
  };

  const levelIcon = (level) => {
    if (level === 'danger') return <FaSkull style={{ color: '#ff6b6b' }} />;
    if (level === 'warning') return <FaExclamationTriangle style={{ color: '#ffa502' }} />;
    if (level === 'success') return <FaCheckCircle style={{ color: '#00ce96' }} />;
    return <FaEye style={{ color: 'var(--text-gray)' }} />;
  };

  const levelClass = (level) => {
    if (level === 'danger') return 'sec-level-danger';
    if (level === 'warning') return 'sec-level-warning';
    if (level === 'success') return 'sec-level-success';
    return 'sec-level-info';
  };

  return (
    <div>
      <div className="admin-page-header">
        <h2 className="admin-page-title"><FaShieldAlt /> Sécurité</h2>
        <button className="btn btn-secondary btn-sm" onClick={fetchData}>
          <FaSync /> Actualiser
        </button>
      </div>

      {actionMsg && (
        <div className="sec-action-msg">{actionMsg}</div>
      )}

      {/* Tabs */}
      <div className="sec-tabs">
        {Object.entries(TAB_LABELS).map(([key, label]) => (
          <button
            key={key}
            className={`sec-tab ${tab === key ? 'active' : ''}`}
            onClick={() => setTab(key)}
          >
            {key === 'overview' && <FaShieldAlt />}
            {key === 'logs' && <FaEye />}
            {key === 'bans' && <FaBan />}
            {key === 'suspicion' && <FaExclamationTriangle />}
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="admin-loading">Chargement...</div>
      ) : (
        <>
          {/* OVERVIEW */}
          {tab === 'overview' && data && (
            <div>
              <div className="admin-stats-grid sec-stats-grid">
                <div className="admin-stat-card sec-stat-danger">
                  <div className="admin-stat-icon" style={{ background: 'rgba(255,107,107,0.1)', color: '#ff6b6b' }}>
                    <FaSkull />
                  </div>
                  <div className="admin-stat-info">
                    <span className="admin-stat-number">{data.recent_attacks}</span>
                    <span className="admin-stat-label">Attaques (24h)</span>
                  </div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-icon" style={{ background: 'rgba(255,165,2,0.1)', color: '#ffa502' }}>
                    <FaBan />
                  </div>
                  <div className="admin-stat-info">
                    <span className="admin-stat-number">{data.active_bans}</span>
                    <span className="admin-stat-label">IPs bannies actives</span>
                  </div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-icon" style={{ background: 'rgba(255,165,2,0.1)', color: '#ffa502' }}>
                    <FaExclamationTriangle />
                  </div>
                  <div className="admin-stat-info">
                    <span className="admin-stat-number">{data.suspect_count}</span>
                    <span className="admin-stat-label">IPs suspectes</span>
                  </div>
                </div>
                <div className="admin-stat-card">
                  <div className="admin-stat-icon" style={{ background: 'rgba(255,107,107,0.1)', color: '#ff6b6b' }}>
                    <FaTimesCircle />
                  </div>
                  <div className="admin-stat-info">
                    <span className="admin-stat-number">{data.locked_out_ips}</span>
                    <span className="admin-stat-label">Login verrouillés</span>
                  </div>
                </div>
              </div>

              <div className="admin-grid-2">
                <div className="admin-card">
                  <h3><FaSkull style={{ color: '#ff6b6b' }} /> Top menaces</h3>
                  {data.top_threats && data.top_threats.length > 0 ? (
                    <div className="admin-list">
                      {data.top_threats.map((t, i) => (
                        <div key={i} className="admin-list-item">
                          <div>
                            <code style={{ marginRight: '8px' }}>{t.ip}</code>
                            <span style={{ fontSize: '0.8rem', color: 'var(--text-gray)' }}>{t.last_reason}</span>
                          </div>
                          <span className="admin-badge" style={{ background: 'rgba(255,107,107,0.15)', color: '#ff6b6b' }}>
                            {t.count} actions
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="admin-empty">Aucune menace detectee</p>
                  )}
                </div>

                <div className="admin-card">
                  <h3><FaShieldAlt style={{ color: 'var(--accent)' }} /> Statistiques</h3>
                  <div className="admin-list">
                    <div className="admin-list-item">
                      <span>Total IPs bannies (historique)</span>
                      <span className="admin-badge">{data.banned_count}</span>
                    </div>
                    <div className="admin-list-item">
                      <span>Bans actifs</span>
                      <span className="admin-badge">{data.active_bans}</span>
                    </div>
                    <div className="admin-list-item">
                      <span>Bans dernières 24h</span>
                      <span className="admin-badge">{data.recent_bans}</span>
                    </div>
                    <div className="admin-list-item">
                      <span>Entrées de log</span>
                      <span className="admin-badge">{data.log_entries}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* LOGS */}
          {tab === 'logs' && Array.isArray(data) && (
            <div className="sec-logs-container">
              {data.length === 0 ? (
                <p className="admin-empty">Aucun log de sécurité</p>
              ) : (
                <div className="sec-logs-list">
                  {data.map((entry, i) => (
                    <div key={i} className={`sec-log-entry ${levelClass(entry.level)}`}>
                      <div className="sec-log-icon">{levelIcon(entry.level)}</div>
                      <div className="sec-log-content">
                        <div className="sec-log-header">
                          <code className="sec-log-ip">{entry.ip}</code>
                          <span className="sec-log-date"><FaClock /> {entry.date}</span>
                        </div>
                        <div className="sec-log-message">{entry.message}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* BANS */}
          {tab === 'bans' && Array.isArray(data) && (
            <div>
              {data.length === 0 ? (
                <p className="admin-empty">Aucune IP bannie</p>
              ) : (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>IP</th>
                        <th>Statut</th>
                        <th>Expire</th>
                        <th>Temps restant</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((ban, i) => (
                        <tr key={i}>
                          <td><code>{ban.ip}</code></td>
                          <td>
                            {ban.active ? (
                              <span className="sec-badge-active"><FaBan /> Banni</span>
                            ) : (
                              <span className="sec-badge-expired">Expiré</span>
                            )}
                          </td>
                          <td>{ban.expires}</td>
                          <td>{ban.active ? formatDuration(ban.remaining) : '-'}</td>
                          <td>
                            {ban.active && (
                              <button
                                className="btn btn-sm btn-restore"
                                onClick={() => handleUnban(ban.ip)}
                                title="Débannir"
                              >
                                <FaUnlock /> Débannir
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* SUSPICION */}
          {tab === 'suspicion' && Array.isArray(data) && (
            <div>
              {data.length === 0 ? (
                <p className="admin-empty">Aucune IP suspecte</p>
              ) : (
                <div className="sec-suspicion-list">
                  {data.map((s, i) => (
                    <div key={i} className="sec-suspicion-card">
                      <div className="sec-suspicion-header">
                        <div>
                          <code className="sec-suspect-ip">{s.ip}</code>
                          <span className={`sec-suspect-count ${s.count >= 10 ? 'critical' : s.count >= 5 ? 'high' : ''}`}>
                            {s.count} actions suspectes
                          </span>
                        </div>
                        <button className="btn btn-sm btn-secondary" onClick={() => handleClearSuspicion(s.ip)}>
                          <FaTrash /> Effacer
                        </button>
                      </div>
                      <div className="sec-suspicion-meta">
                        <span>Première vue : {s.first_seen}</span>
                        <span>Dernière vue : {s.last_seen}</span>
                      </div>
                      {s.reasons.length > 0 && (
                        <div className="sec-suspicion-reasons">
                          <strong>Raisons :</strong>
                          <ul>
                            {s.reasons.map((r, j) => (
                              <li key={j}>{r}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminSecurity;
